import * as analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import BenchlingConfig from 'BenchlingConfig';
import {ClickableContextMenuOption, ContextMenuDisplayGroup} from 'components/ContextMenu';
import {ActionMethod, ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import sortTableByColumn from 'notebook/structured-tables/column-sorting/sortTableByColumn';
import {ColumnDataType, guessColumnDataType} from 'notebook/structured-tables/columns/ColumnDataType';
import selectionHasSingleInput from 'notebook/structured-tables/single-inputs/util/selectionHasSingleInputs';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import isMergedCell from 'notebook/structured-tables/util/isMergedCell';
import rowHasUnsubmittedChanges from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import {Table} from 'notebook/tables/constants/TableConstants';
import {SelectionRange} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import TableUtil from 'notebook/tables/TableUtil';
import {isRowArchived} from 'notebook/tables/util/isRowArchived';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';

export const SORT_ASCENDING_ID = 'sort-ascending';
export const SORT_DESCENDING_ID = 'sort-descending';

export default function getColumnSortingContextMenuOptions({
  selectionRange,
  fileId,
  table,
  computedTable,
  structuredTable,
  tableIsLocked,
  onSort,
  fileAnalyticsParams,
  noteIsProtected,
}: {
  selectionRange: SelectionRange | null;
  fileId: string | null;
  table: Table;
  computedTable: Array<Array<EvaluatedCell>> | undefined;
  structuredTable: UnknownStructuredTable;
  tableIsLocked: boolean;
  onSort: (newToOldIndices: Array<number>) => void;
  fileAnalyticsParams: FileAnalyticsParams | null;
  noteIsProtected?: boolean;
}): Array<ClickableContextMenuOption> {
  if (
    // Disallow for legacy workflows
    (fileId && (APIUtil.isStageEntry(fileId) || APIUtil.isStageEntryTemplate(fileId))) ||
    !selectionRange ||
    // Disallow for single inputs because they don't have anything interesting to sort
    selectionHasSingleInput(structuredTable, selectionRange)
  ) {
    return [];
  }

  // Disallow if selecting multiple columns
  const selectedMultipleColumns = selectionRange.minIndex !== selectionRange.maxIndex;
  // Disallow if only one row (sorting would have no effect)
  const hasSingleRow = table.table.length <= 1;
  const disableFillInOnlyColumnSorting =
    noteIsProtected &&
    !structuredTable.isConfig &&
    BenchlingConfig.ELN_DISABLE_FILL_IN_ONLY_ENTRIES_COLUMN_SORTING;

  const disabledReason = disableFillInOnlyColumnSorting
    ? 'Cannot sort columns in fill-in only entries'
    : selectedMultipleColumns
      ? 'Cannot sort by multiple columns'
      : hasSingleRow
        ? 'Cannot sort on a table with only one row'
        : undefined;

  const columnIndex = selectionRange.minIndex;
  const columnDefinition = table.columnConstraints[columnIndex]?.columnDefinition;
  const columnType = columnDefinition && guessColumnDataType(columnDefinition, structuredTable)?.type;

  let sortTitle: [string, string];
  if (columnType === ColumnDataType.INTEGER || columnType === ColumnDataType.DECIMAL) {
    sortTitle = ['lowest', 'highest'];
  } else if (columnType === ColumnDataType.BOOLEAN) {
    sortTitle = ['false', 'true'];
  } else if (columnType === ColumnDataType.DATE || columnType === ColumnDataType.DATETIME) {
    sortTitle = ['oldest', 'newest'];
  } else {
    sortTitle = ['A', 'Z'];
  }

  function getSortOption(reverse: boolean): ClickableContextMenuOption {
    return {
      name: `Sort column ${(reverse ? [...sortTitle].reverse() : sortTitle).join(' to ')}`,
      id: reverse ? SORT_DESCENDING_ID : SORT_ASCENDING_ID,
      displayGroup: ContextMenuDisplayGroup.BOTTOM,
      hide: tableIsLocked,
      disabled: Boolean(disabledReason),
      tooltip: disabledReason,
      click: () => {
        // Finds the first column index that is not a single input.
        const singleInputBoundary: number = TableUtil.findSingleInputBoundaryIndex(table);
        // Disallow if there are any merged cells in the table except for the single input columns.
        // This is an expensive check, so in order to avoid slowing down the right click menu, we do this in the click handler instead of disabling the option.
        const hasMergedCell = table.table.some((row) =>
          row.slice(singleInputBoundary).some((cell) => isMergedCell(cell))
        );
        const hasMergedCellInColumn =
          hasMergedCell && table.table.some((row) => isMergedCell(row[columnIndex]));

        const analyticsParams = {
          actionOrigin: structuredTableActionOrigin(structuredTable),
          actionMethod: ActionMethod.STRUCTURED_TABLE_COLUMN_MENU,
          ...fileAnalyticsParams,
          // Structured table information
          is_config: structuredTable.isConfig,
          structured_table_api_identifier: structuredTable.apiId ?? null,
          structured_table_provenance_uuid: structuredTable.provenanceUuid,
          structured_table_kind: structuredTable.kind,
          structured_table_mode: (structuredTable.metadata as {mode?: string}).mode ?? null,
          structured_table_metadata: JSON.stringify(structuredTable.metadata),
          structured_table_num_rows: structuredTable.rows.length,
          structured_table_num_columns: structuredTable.columns.length,
          num_rows_unsubmitted: computedTable
            ? structuredTable.rows.filter((row, idx) =>
                rowHasUnsubmittedChanges(
                  computedTable[idx],
                  row,
                  getStructuredTableAdapterFromTable(structuredTable).getColumnsForRow(structuredTable, row),
                  structuredTable.schemas
                )
              ).length
            : null,
          num_rows_archived: structuredTable.rows.filter(
            (row, idx) => isRowArchived(row) || (table.rowData && isRowArchived(table.rowData[idx]))
          ).length,
          // Sorting information
          columnIndex,
          columnType,
          is_reverse: reverse,
          // Table information
          hasMergedCell,
          hasMergedCellInColumn,
        };

        if (hasMergedCell) {
          // TODO(BNCH-94958): Allow sorting in certain tables with merged cells
          notifications.error('Cannot sort on a table with a merged cell.');
        } else {
          const {sortedIndices, analyticsParams: partialAnalyticsParams} = sortTableByColumn(
            table,
            computedTable,
            columnIndex,
            {
              reverse,
            }
          );
          onSort(sortedIndices);
          Object.assign(analyticsParams, partialAnalyticsParams);
        }

        analytics.trackEvent(ELN_ANALYTICS_ACTIONS.SORTED_TABLE_BY_COLUMN, analyticsParams);
      },
      dataTestAttrs: {
        component: 'ContextMenu',
        element: 'option',
        key: reverse ? 'sortColumnReverse' : 'sortColumn',
      },
    };
  }
  return [getSortOption(false), getSortOption(true)];
}
